import { Loader2, Inbox, Star } from "lucide-react";
import { useRef, useState } from "react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/format";
import { statusMeta } from "@/lib/labels";
import { toast } from "sonner";

export function Loader({ label = "Chargement..." }) {
  return (
    <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="loader">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> {label}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-6" data-testid="empty-state">
      <div className="w-14 h-14 rounded-2xl bg-[#FDF3EE] flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-[#C85A32]" />
      </div>
      <h3 className="font-display text-xl font-bold text-slate-800">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function StatusBadge({ map, value, className = "" }) {
  const m = statusMeta(map, value);
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${m.color} ${className}`} data-testid={`status-${value}`}>
      {m.label}
    </span>
  );
}

export function StarRating({ value = 0, onChange, size = 22, readOnly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange?.(n)}
          data-testid={`star-${n}`}
          className={readOnly ? "cursor-default" : "cursor-pointer transition-transform active:scale-90"}
        >
          <Star
            style={{ width: size, height: size }}
            className={(hover || value) >= n ? "fill-[#E08B00] text-[#E08B00]" : "text-slate-300"}
          />
        </button>
      ))}
    </div>
  );
}

// Uploads files to object storage and returns absolute file URLs.
export function ImageUploader({ value = [], onChange, label = "Ajouter des photos", multiple = true, testid = "uploader", accept = "image/*" }) {
  const ref = useRef();
  const dragIdx = useRef(null);
  const [busy, setBusy] = useState(false);
  const [overIdx, setOverIdx] = useState(null);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(fileUrl(data.url));
      }
      onChange?.(multiple ? [...value, ...urls] : urls.slice(0, 1));
    } catch {
      toast.error("Échec du téléversement");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= value.length || from === to) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange?.(next);
  };

  const canDrag = multiple && value.length > 1;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((u, i) => (
          <div
            key={i}
            draggable={canDrag}
            onDragStart={() => { dragIdx.current = i; }}
            onDragOver={(e) => { if (canDrag) { e.preventDefault(); setOverIdx(i); } }}
            onDragLeave={() => setOverIdx((o) => (o === i ? null : o))}
            onDrop={(e) => { e.preventDefault(); if (dragIdx.current !== null) move(dragIdx.current, i); dragIdx.current = null; setOverIdx(null); }}
            onDragEnd={() => { dragIdx.current = null; setOverIdx(null); }}
            className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 group transition ${overIdx === i ? "border-[#C85A32] ring-2 ring-[#C85A32]/30" : "border-slate-200"} ${canDrag ? "cursor-move" : ""}`}
            data-testid={`${testid}-thumb-${i}`}
          >
            <img src={u} alt="" className="w-full h-full object-cover pointer-events-none" />
            {multiple && i === 0 && (
              <span className="absolute bottom-0 inset-x-0 bg-[#C85A32] text-white text-[9px] font-bold text-center py-0.5" data-testid={`${testid}-cover-${i}`}>Couverture</span>
            )}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 text-xs leading-none"
              data-testid={`${testid}-remove-${i}`}
            >×</button>
            {canDrag && (
              <span className="absolute top-0.5 left-0.5 bg-black/50 text-white rounded px-1 text-[8px] font-bold opacity-0 group-hover:opacity-100 transition pointer-events-none">glisser</span>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="text-sm font-semibold text-[#C85A32] border border-dashed border-[#C85A32]/50 rounded-lg px-4 py-2 hover:bg-[#FDF3EE] transition disabled:opacity-50"
        data-testid={`${testid}-btn`}
      >
        {busy ? "Téléversement..." : label}
      </button>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={handle} data-testid={`${testid}-input`} />
    </div>
  );
}
